"use client";

import { useState, useRef } from "react";
import Image from "next/image";
import Chat from "../Chat/index";
import {
  Phone,
  Mail,
  Globe,
  MapPin,
  Facebook,
  Linkedin,
  MessageCircle,
  QrCode,
  Share2,
  PhoneCall,
  Calendar,
  Contact2,
} from "lucide-react";
import { downloadVCard } from "@/lib/vcard";
import MapModal from "@/components/MapModal";
import { getSocialIcon } from "@/lib/social-icons";
import { SocialLink } from "@/lib/types";
import { ExtraInfo } from "@/lib/types";

export type BusinessCardProps = {
  title:string,
  firstname:string,
  lastname:string,
  slogan:string,
  avatar:string,
  banner:string,
  company:string,
  position:string,
  email:string,
  phone:string,
  website:string,
  address:string,
  social:SocialLink[],
  color:string,
  geolocation:string,
  info:ExtraInfo[],
  bank:string,
  qrcode:string,
  isPersonal:boolean,
  type:string,
  style:string,
  aiagent:string
};

export default function BusinessCard(props: BusinessCardProps) {
  const {
    title,
    firstname,
    lastname,
    slogan,
    avatar,
    banner,
    company,
    position,
    email,
    phone,
    website,
    address,
    social,
    color, 
    geolocation,
    info,
    bank,
    qrcode,
    isPersonal,
    type,
    style,
    aiagent,
  } = props;


  const [isMapOpen, setMapOpen] = useState(false);

  const handleSaveContact = () => {
    downloadVCard({ firstname, lastname, org: company, title, phone, email, website, address });
  };

  const handleShare = async () => {
    const shareData = {
      title: `${firstname} ${lastname} – ${company}`,
      text: `${firstname} ${lastname} | ${title} tại ${company}`,
      url: website,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch {
        /* người dùng huỷ chia sẻ */
      }
    } else {
      await navigator.clipboard.writeText(website);
    }
  };

  const qrData = encodeURIComponent(qrcode ?? website);
  const qrImageSrc = `https://api.qrserver.com/v1/create-qr-code/?size=128x128&margin=2&color=0-119-187&data=${qrData}`;

  const today = new Intl.DateTimeFormat("vi-VN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date());

  const arrGeo = geolocation.split(",");

  return (
    <div className="relative w-full lg:max-w-[500px] shrink-0 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
        style={{ borderColor: color }}
    >
      {/* 1. Banner ảnh + 2. Avatar overlap bên phải */}
      <div className="relative mb-3 w-full overflow-hidden bg-slate-200"
        style={{
          height:"180px"
        }}>
        <Image
          src={banner}
          alt={`Ảnh banner của ${company}`}
          fill
          sizes="300px"
          className="object-cover"
          priority
        />
      </div>

      {/* Avatar: bên phải, chồng lên ranh giới banner / nội dung */}
        <div className="absolute right-5 h-16 w-16 overflow-hidden rounded-full border-[3px] border-white bg-white shadow-sm"
            style={{
              top: "130px",
              width:"80px",
              height:"80px" 
            }} >
          <Image
            src={avatar}
            alt={`Ảnh đại diện của ${title}`}
            fill
            sizes="64px"
            className="object-cover"
          />
        </div>

      {/* Tên / chức danh / công ty */}
      <div className="-mt-1.5 px-5 pb-3 ">
        <div className="border-l-2 border-dotted border-slate-300 pl-3">
          <h1 className="font-display text-lg font-semibold text-slate-900">{firstname} {lastname}</h1>
          <p className="mt-0.5 text-sm text-slate-600">{title}</p>
          <p className="mt-0.5 text-sm italic text-slate-400">{company}</p>
          <p className="mt-1 text-xs italic text-slate-400"></p>
        </div>
      </div>

      {/* Danh sách liên hệ */}
      <div className="flex flex-col gap-2.5 px-5 pb-3">
        <a href={`mailto:${email}`} className="flex items-center gap-2.5">
          <IconBubble icon={Mail} color={color} />
          <span className="truncate text-[13px] text-slate-800">{email}</span>
        </a>

        <a href={`tel:${phone}`} className="flex items-center gap-2.5">
          <IconBubble icon={Phone} color={color} />
          <span className="truncate text-[13px] text-slate-800">{phone}</span>
        </a>

        <a href={website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5">
          <IconBubble icon={Globe} color={color} />
          <span className="truncate text-[13px] text-slate-800">{website}</span>
        </a>

        {/* 4. Click icon map -> mở popup Google Map theo toạ độ lat/lng */}
        <button
            type="button"
            onClick={() => setMapOpen(true)}
            className="flex items-center gap-2.5 text-left"
        >
            <IconBubble icon={MapPin} color={color} />
            <span className="truncate text-[13px] text-slate-800">{address}</span>
        </button>

        <div>
          { Array.isArray(info) ? info.map((item, i)  => {
          return (
            <div key={`${item.label}-${i}`}><a
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              className="gap-2.5"
            >
              <strong className="text-[13px] text-slate-800"
                style={{ color: color, textTransform: "capitalize" }}>{item.label}</strong>
              <p className="text-[13px] text-slate-800"
                style={{ textAlign: "justify" }}>{item.content}</p>
            </a></div>
          );
        }) : null }
        </div>

        <hr/>

        <div className="flex justify-between">
        {social.map((social, i) => {
          const { icon: Icon, color } = getSocialIcon(social.type);
          return (
            <a
              key={`${social.type}-${i}`}
              href={social.url}
              target="_blank"
              rel="noopener noreferrer"
              className="gap-2.5"
            >
              <span
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
                style={{ backgroundColor: color }}
              >
                <Icon size={15} className="text-white" />
              </span>
            </a>
          );
        })}
        </div>
      </div>

      {/* QR code */}
      <div className="flex items-center gap-3 border-t border-slate-100 px-5 py-3">
        <Image
          src={qrImageSrc}
          alt="Mã QR liên kết nhanh tới website"
          width={64}
          height={64}
          unoptimized
          className="rounded-md"
        />
        <p className="flex items-center gap-1 text-xs text-slate-500">
          <QrCode size={14} className="text-[#0077bb]" />
          Quét để lưu danh bạ hoặc mở website.
        </p>
      </div>

      {/* Action buttons */}
      <div className="grid grid-cols-2 gap-2 px-5 pb-4">
        <button
          onClick={handleSaveContact}
          className="col-span-2 flex items-center justify-center gap-2 rounded-lg bg-[#0077bb] py-2.5 text-sm font-medium text-white transition hover:bg-[#00669e] active:scale-[0.98]"
          style={{ backgroundColor: color }}
        >
          <Contact2 size={16} />
          Lưu danh bạ
        </button>
        <button
          className="col-span-2 flex items-center justify-center gap-2 rounded-lg bg-[#0077bb] py-2.5 text-sm font-medium text-white transition hover:bg-[#00669e] active:scale-[0.98]"
            style={{ backgroundColor: color }}
        >
          Chat
        </button>
        <a
          href={`tel:${phone}`}
          className="flex items-center justify-center gap-1 rounded-lg border border-slate-200 py-2 text-xs font-medium text-slate-700 transition hover:bg-slate-50"
        >
          <PhoneCall size={14} />
          Gọi
        </a>
        <button
          onClick={handleShare}
          className="flex items-center justify-center gap-1 rounded-lg border border-slate-200 py-2 text-xs font-medium text-slate-700 transition hover:bg-slate-50"
        >
          <Share2 size={14} />
          Chia sẻ
        </button>
      </div>

      {/* Footer: tag + ngày */}
      <div className="flex items-center justify-between border-t border-slate-100 px-5 py-3">
        <span className="text-sm font-medium text-slate-900">WORK</span>
        <span className="flex items-center gap-1 text-xs text-slate-400">
          <Calendar size={13} />
          {today}
        </span>
      </div>

      <MapModal isOpen={isMapOpen} onClose={() => setMapOpen(false)} lat={parseInt(arrGeo[0])} lng={parseInt(arrGeo[1])} address={address} />
    </div>
  );
}

function IconBubble({ icon: Icon, color }: { icon: typeof Phone, color: string }) {
  return (
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
            style={{ backgroundColor: color }}>
      <Icon size={15} className="text-white" />
    </span>
  );
}
